import React, { useState } from 'react';
import { Bell, Mail, MessageSquare, Truck, AlertTriangle, Clock, Volume2, Smartphone, Monitor } from 'lucide-react';

const NotificationSettings = ({ onChange }) => {
  const [notifications, setNotifications] = useState({
    global: {
      enabled: true,
      sound: true,
      vibration: true,
      doNotDisturb: false,
      quietHours: {
        enabled: false,
        start: '22:00',
        end: '08:00'
      }
    },
    channels: {
      browser: true,
      email: true,
      sms: false,
      mobile: true
    },
    categories: {
      alerts: {
        critical: { browser: true, email: true, sms: true, mobile: true, sound: true },
        warnings: { browser: true, email: true, sms: false, mobile: true, sound: true },
        info: { browser: true, email: false, sms: false, mobile: false, sound: false }
      },
      fleet: {
        vehicleBreakdown: { browser: true, email: true, sms: true, mobile: true, sound: true },
        maintenanceDue: { browser: true, email: true, sms: false, mobile: true, sound: false },
        inspectionRequired: { browser: true, email: true, sms: false, mobile: true, sound: false }
      },
      drivers: {
        hosViolation: { browser: true, email: true, sms: true, mobile: true, sound: true },
        documentExpiring: { browser: true, email: true, sms: false, mobile: true, sound: false },
        driverUnavailable: { browser: true, email: false, sms: false, mobile: true, sound: false }
      },
      routes: {
        deliveryDelayed: { browser: true, email: true, sms: false, mobile: true, sound: false },
        routeCompleted: { browser: false, email: false, sms: false, mobile: false, sound: false },
        emergencyStop: { browser: true, email: true, sms: true, mobile: true, sound: true }
      },
      system: {
        systemMaintenance: { browser: true, email: true, sms: false, mobile: false, sound: false },
        updates: { browser: true, email: false, sms: false, mobile: false, sound: false },
        backupStatus: { browser: false, email: true, sms: false, mobile: false, sound: false }
      }
    }
  });

  const notificationCategories = [
    {
      id: 'alerts',
      title: 'Critical Alerts',
      icon: AlertTriangle,
      color: 'text-red-600',
      items: [
        { key: 'critical', label: 'Critical Alerts', description: 'Emergency situations requiring immediate attention' },
        { key: 'warnings', label: 'Warnings', description: 'Important issues that need attention' },
        { key: 'info', label: 'Information', description: 'General updates and information' }
      ]
    },
    {
      id: 'fleet',
      title: 'Fleet Management',
      icon: Truck,
      color: 'text-blue-600',
      items: [
        { key: 'vehicleBreakdown', label: 'Vehicle Breakdown', description: 'Vehicle breakdowns and emergencies' },
        { key: 'maintenanceDue', label: 'Maintenance Due', description: 'Scheduled maintenance reminders' },
        { key: 'inspectionRequired', label: 'Inspection Required', description: 'Vehicle inspection deadlines' }
      ]
    },
    {
      id: 'drivers',
      title: 'Driver Management',
      icon: Clock,
      color: 'text-green-600',
      items: [
        { key: 'hosViolation', label: 'HOS Violations', description: 'Hours of service compliance issues' },
        { key: 'documentExpiring', label: 'Document Expiring', description: 'License and certificate expirations' },
        { key: 'driverUnavailable', label: 'Driver Unavailable', description: 'Driver availability status changes' }
      ]
    },
    {
      id: 'routes',
      title: 'Route & Delivery',
      icon: MessageSquare,
      color: 'text-purple-600',
      items: [
        { key: 'deliveryDelayed', label: 'Delivery Delayed', description: 'Route delays and schedule changes' },
        { key: 'routeCompleted', label: 'Route Completed', description: 'Successful route completions' },
        { key: 'emergencyStop', label: 'Emergency Stop', description: 'Unplanned stops and emergencies' }
      ]
    },
    {
      id: 'system',
      title: 'System Updates',
      icon: Monitor,
      color: 'text-gray-600',
      items: [
        { key: 'systemMaintenance', label: 'System Maintenance', description: 'Scheduled maintenance windows' },
        { key: 'updates', label: 'Software Updates', description: 'New features and improvements' },
        { key: 'backupStatus', label: 'Backup Status', description: 'Data backup notifications' }
      ]
    }
  ];

  const channels = [
    { key: 'browser', label: 'Browser', icon: Monitor, description: 'In-app notifications' },
    { key: 'email', label: 'Email', icon: Mail, description: 'Email notifications' },
    { key: 'sms', label: 'SMS', icon: MessageSquare, description: 'Text messages' },
    { key: 'mobile', label: 'Mobile App', icon: Smartphone, description: 'Push notifications' }
  ];

  const handleGlobalChange = (key, value) => {
    setNotifications(prev => ({
      ...prev,
      global: {
        ...prev.global,
        [key]: value
      }
    }));
    onChange();
  };

  const handleQuietHoursChange = (key, value) => {
    setNotifications(prev => ({
      ...prev,
      global: {
        ...prev.global,
        quietHours: {
          ...prev.global.quietHours,
          [key]: value
        }
      }
    }));
    onChange();
  };

  const handleChannelChange = (channel, enabled) => {
    setNotifications(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: enabled
      }
    }));
    onChange();
  };

  const handleCategoryChange = (category, item, channel, value) => {
    setNotifications(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: {
          ...prev.categories[category],
          [item]: {
            ...prev.categories[category][item],
            [channel]: value
          }
        }
      }
    }));
    onChange();
  };

  return (
    <div className="space-y-8">
      {/* Global Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Bell className="h-5 w-5 mr-2 text-blue-600" />
          Global Settings
        </h3>
        <div className="space-y-4">
          <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700">Enable all notifications</span>
              <p className="text-xs text-gray-500">Master switch for all notification types</p>
            </div>
            <input
              type="checkbox"
              checked={notifications.global.enabled}
              onChange={(e) => handleGlobalChange('enabled', e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700 flex items-center">
                <Volume2 className="h-4 w-4 mr-2" />
                Sound notifications
              </span>
              <p className="text-xs text-gray-500">Play sound for important alerts</p>
            </div>
            <input
              type="checkbox"
              checked={notifications.global.sound}
              onChange={(e) => handleGlobalChange('sound', e.target.checked)}
              disabled={!notifications.global.enabled}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <span className="text-sm font-medium text-gray-700">Vibration (mobile)</span>
              <p className="text-xs text-gray-500">Vibrate for notifications on mobile devices</p>
            </div>
            <input
              type="checkbox"
              checked={notifications.global.vibration}
              onChange={(e) => handleGlobalChange('vibration', e.target.checked)}
              disabled={!notifications.global.enabled}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
          </label>

          <div className="p-4 border border-gray-200 rounded-lg">
            <label className="flex items-center justify-between mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Quiet hours</span>
                <p className="text-xs text-gray-500">Suppress non-critical notifications during specified hours</p>
              </div>
              <input
                type="checkbox"
                checked={notifications.global.quietHours.enabled}
                onChange={(e) => handleQuietHoursChange('enabled', e.target.checked)}
                disabled={!notifications.global.enabled}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            {notifications.global.quietHours.enabled && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Start time</label>
                  <input
                    type="time"
                    value={notifications.global.quietHours.start}
                    onChange={(e) => handleQuietHoursChange('start', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">End time</label>
                  <input
                    type="time"
                    value={notifications.global.quietHours.end}
                    onChange={(e) => handleQuietHoursChange('end', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification Channels */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Channels</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {channels.map((channel) => {
            const Icon = channel.icon;
            return (
              <label
                key={channel.key}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  notifications.channels[channel.key]
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={notifications.channels[channel.key]}
                  onChange={(e) => handleChannelChange(channel.key, e.target.checked)}
                  disabled={!notifications.global.enabled}
                  className="sr-only"
                />
                <Icon className="h-6 w-6 mx-auto mb-2 text-gray-600" />
                <div className="text-sm font-medium text-gray-900 text-center">{channel.label}</div>
                <div className="text-xs text-gray-500 text-center mt-1">{channel.description}</div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Notification Categories */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Categories</h3>
        <div className="space-y-6">
          {notificationCategories.map((category) => {
            const CategoryIcon = category.icon;
            return (
              <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                  <h4 className={`font-medium flex items-center ${category.color}`}>
                    <CategoryIcon className="h-4 w-4 mr-2" />
                    {category.title}
                  </h4>
                </div>
                <div className="divide-y divide-gray-200">
                  {category.items.map((item) => (
                    <div key={item.key} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="text-sm font-medium text-gray-900">{item.label}</h5>
                          <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {channels.map((channel) => (
                          <label key={channel.key} className="flex items-center text-xs">
                            <input
                              type="checkbox"
                              checked={notifications.categories[category.id][item.key][channel.key] || false}
                              onChange={(e) => handleCategoryChange(category.id, item.key, channel.key, e.target.checked)}
                              disabled={!notifications.global.enabled || !notifications.channels[channel.key]}
                              className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                            />
                            <span className="text-gray-700">{channel.label}</span>
                          </label>
                        ))}
                        <label className="flex items-center text-xs">
                          <input
                            type="checkbox"
                            checked={notifications.categories[category.id][item.key].sound || false}
                            onChange={(e) => handleCategoryChange(category.id, item.key, 'sound', e.target.checked)}
                            disabled={!notifications.global.enabled || !notifications.global.sound}
                            className="h-3 w-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
                          />
                          <span className="text-gray-700">Sound</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Test Notification */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Test Notifications</h4>
        <p className="text-sm text-blue-700 mb-3">
          Send a test notification to verify your settings are working correctly.
        </p>
        <button
          onClick={() => console.log('Test notification sent')}
          className="btn-primary"
        >
          <Bell className="h-4 w-4 mr-2" />
          Send Test Notification
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
